import { Descriptions } from 'antd';
import { Fragment, useState } from 'react';

import ContactForm from './ContactTracingForm';
import { RegistrationBlock } from './Shared';
import styles from '../index.module.less';

import {
    Alert,
    ApplePassButton,
    CallToAction,
    Card,
    Col,
    Divider,
    GooglePassButton,
    Logo,
    Row
} from '$HVUI';
import { flexibleDateString, fullNameFromProfile } from '$Utils';

const MinimalResultDisplay = props => {
    const {
        applePassUUID,
        contactTraceReminder,
        googlePassUUID,
        showApplePassDownload,
        showContactTracing,
        showGooglePassDownload,
        token,
        visit,
        visit: { first_name, last_name, birthdate }
    } = props;

    const recordsRequest = visit.records_request || {};
    const results = recordsRequest?.results;
    const scheduledVisit = visit.scheduled_visit || {};
    const hasSingleNextStep = results.filter(r => !!r.next_steps).length === 1;

    const showResultsPassDownloadAlert =
        showGooglePassDownload || showApplePassDownload;

    const resultsPassDownloadAlert = () => {
        return (
            <Alert
                message={
                    <>
                        <p
                            style={{
                                textAlign: 'center',
                                fontWeight: 'bold'
                            }}
                        >
                            {' '}
                            Download your COVID-19 test result to your mobile
                            phone for your convenience!
                        </p>
                        <span>
                            {showApplePassDownload && applePassUUID && (
                                <ApplePassButton
                                    first_name={first_name}
                                    last_name={last_name}
                                    birthdate={birthdate}
                                    public_uuid={applePassUUID}
                                    queryType="token"
                                />
                            )}
                            {showGooglePassDownload && googlePassUUID && (
                                <GooglePassButton
                                    first_name={first_name}
                                    last_name={last_name}
                                    birthdate={birthdate}
                                    public_uuid={googlePassUUID}
                                    queryType="token"
                                />
                            )}
                        </span>
                    </>
                }
            ></Alert>
        );
    };

    const resultDisplay = (result, index) => {
        const releaseDate = flexibleDateString(
            result.result_datetime,
            null,
            'MES'
        );
        return (
            <Fragment key={index}>
                <Divider />
                <Row style={{ fontSize: '1.75em', marginBottom: '0.5em' }}>
                    <Col span={12}>{result.condition}</Col>
                    <Col
                        span={12}
                        style={{ fontWeight: 'bold', textAlign: 'right' }}
                    >
                        {result.display_value}
                    </Col>
                </Row>
                <Row style={{ fontSize: '120%', marginBottom: '0.5em' }}>
                    <Col span={24}>Test Type: {result.test_name}</Col>
                </Row>

                {index == 0 && !contactTraceReminder && (
                    <Row style={{ fontSize: '120%', marginBottom: '1em' }}>
                        <Col span={24}>Specimen Date: {releaseDate}</Col>
                    </Row>
                )}
                {index == 0 && showContactTracing && (
                    <ContactForm token={token} />
                )}

                {showResultsPassDownloadAlert && (
                    <Row>
                        <Col>{resultsPassDownloadAlert()}</Col>
                    </Row>
                )}

                {!hasSingleNextStep && result.next_steps && (
                    <>
                        <Divider dashed={true} />
                        {nextStepDisplay(result)}
                    </>
                )}
            </Fragment>
        );
    };

    const nextStepDisplay = (result, index) => {
        return (
            <Fragment key={index}>
                {!!result.next_steps && (
                    <div
                        className={styles['about-results']}
                        dangerouslySetInnerHTML={{ __html: result.next_steps }}
                    ></div>
                )}
            </Fragment>
        );
    };
    const notifyReminder = () => {
        return (
            <>
                {![13045, 13053].includes(visit?.organization?.id) &&
                    contactTraceReminder && (
                        <Alert
                            style={{ margin: '1em 0 0' }}
                            message={
                                <>
                                    <p
                                        id="reminder-header"
                                        style={{ fontSize: '1.25em' }}
                                    >
                                        <strong>
                                            Protect your loved ones and do your
                                            part to slow the spread of COVID-19
                                        </strong>
                                    </p>
                                    <p>
                                        Use the form below to anonymously send
                                        your close contacts free COVID-19
                                        testing resources and important
                                        information on how to protect their
                                        loved ones.
                                    </p>
                                    <p>
                                        Notifying your close contacts is an
                                        important way to:
                                    </p>
                                    <ul>
                                        <li>Protect your loved ones</li>
                                        <li>Stop people from getting sick</li>
                                        <li> Help end COVID-19</li>
                                    </ul>
                                </>
                            }
                        />
                    )}
            </>
        );
    };

    const fullName = fullNameFromProfile(visit);
    const birthdateFormatted = flexibleDateString(birthdate, null, 'MES');
    return (
        <div className={styles['wrapper']}>
            <Card className={styles['card']}>
                <Row>
                    <Col span={12}>
                        <Logo style={{ width: '100%' }} />
                    </Col>
                    <Col span={12} />
                </Row>
                {notifyReminder(results[0])}
                <Divider />
                <h1 id="exp-result-name" className={styles['name-display']}>
                    {fullName}
                </h1>
                <h2 id="exp-result-birthdate" className={styles['dob-display']}>
                    Birthdate: {birthdateFormatted}
                </h2>
                {scheduledVisit.external_appointment_id && (
                    <h2
                        id="exp-result-appt-id"
                        className={styles['appointment-label']}
                    >
                        Appointment ID: {scheduledVisit.external_appointment_id}
                    </h2>
                )}
                {scheduledVisit.external_appointment_date && (
                    <h2
                        id="exp-result-appt-date"
                        className={styles['appointment-label']}
                    >
                        Appointment Date:{' '}
                        {scheduledVisit.external_appointment_date}
                    </h2>
                )}

                {results.map(resultDisplay)}

                <RegistrationBlock {...props} />
                {hasSingleNextStep && (
                    <>
                        <Divider />
                        {nextStepDisplay(results[0])}
                    </>
                )}

                <Divider />
                <Descriptions
                    bordered
                    column={{ xs: 1, sm: 1, md: 1, lg: 3 }}
                    layout="vertical"
                    className={styles['descriptions']}
                >
                    <Descriptions.Item label={<b>Collection location</b>}>
                        {scheduledVisit.external_facility_name}
                    </Descriptions.Item>
                    <Descriptions.Item label={<b>Referring Physician</b>}>
                        {recordsRequest.referring_physician_name ||
                            '(Not Available)'}
                    </Descriptions.Item>
                    <Descriptions.Item label={<b>Testing Lab Name</b>}>
                        {results[0].lab_name || '(Not Available)'}
                    </Descriptions.Item>
                </Descriptions>
                <Divider />
                <p className={styles['wrap-text']}>
                    Is any of your information incorrect?
                </p>
                <CallToAction
                    level="primary"
                    size="narrow"
                    text="contact us"
                    target="_blank"
                    href="https://healthvana.com/contact/"
                />
            </Card>
        </div>
    );
};

export default MinimalResultDisplay;
